import Toast from 'components/toast/Toast'
import { axiosDefaultConfig } from 'constants/axios'
import { useFormik } from 'formik'
import {
  AdminsApi,
  AdminsUserListResponseItem,
  Configuration,
  DepartmentPointDisbursementRequestEnum,
  DisbursementRecipientSelectionTypeEnum,
  GetListLoyaltyPointDisbursementRequestResponseItem,
  LoyaltyApi,
  LoyaltyRequestDataDisbursementType,
  LoyaltyRequestPointDisbursementRequest,
  LoyaltyRequestPointDisbursementRequestRecipients,
  GetLoyaltyPointDisbursementRequestResponse,
  GetLoyaltyPointDisbursementRequestResponseRecipients,
  PaginationResponse,
} from 'generated/api'
import debounce from 'helpers/debounce'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export default function useSawitPoin() {
  const { toast, toastContainer } = Toast()
  const navigate = useNavigate()
  const axiosConfig = new Configuration(axiosDefaultConfig)
  const loyaltyApi = new LoyaltyApi(axiosConfig)
  const adminsApi = new AdminsApi(axiosConfig)
  const [disbursementTypes, setDisbursementTypes] = useState<LoyaltyRequestDataDisbursementType[]>(
    [],
  )
  const [currentPage, setCurrentPage] = useState(1)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const [search, setSearch] = useState('')
  const [totalPages, setTotalPages] = useState(0)
  const [totalUsers, setTotalUsers] = useState(0)
  const [loadingUser, setLoadingUser] = useState(false)
  const [modalConfirm, setModalConfirm] = useState(false)
  const [modalReject, setModalReject] = useState(false)
  const [listUsers, setListUsers] = useState<AdminsUserListResponseItem[]>([])
  const [excludeUsers, setExcludeUsers] = useState<AdminsUserListResponseItem[]>([])
  const [includeUsers, setIncludeUsers] = useState<AdminsUserListResponseItem[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [listUsersSelected, setListUsersSelected] = useState<AdminsUserListResponseItem[]>([])
  const [listSawitPoin, setListSawitPoin] = useState<
    GetListLoyaltyPointDisbursementRequestResponseItem[]
  >([])
  const [poinDetail, setPoinDetail] = useState<GetLoyaltyPointDisbursementRequestResponse>({
    requestDate: 0,
    applicantsName: '',
    department: '' as DepartmentPointDisbursementRequestEnum,
    disbursementType: '',
    purpose: '',
    title: '',
    description: '',
    totalPoint: 0,
    totalRecipients: 0,
    recipients: [] as GetLoyaltyPointDisbursementRequestResponseRecipients[],
    pagination: {} as PaginationResponse,
    id: '',
    status: '',
    reason: '',
  })

  const [formData, setDataForm] = useState<LoyaltyRequestPointDisbursementRequest>({
    department: '' as DepartmentPointDisbursementRequestEnum,
    disbursementTypeId: '',
    purpose: '',
    description: '',
    title: '',
    point: 0,
    selectionType: '' as DisbursementRecipientSelectionTypeEnum,
    recipients: [] as Array<LoyaltyRequestPointDisbursementRequestRecipients>,
  })
  const [loadingFetch, setLoadingFetch] = useState<boolean>(false)
  const [failedFetch, setFailedFetch] = useState<boolean>(false)

  const formik = useFormik({
    initialValues: formData,
    validateOnBlur: true,
    onSubmit: async values => {
      postSawitPoin(values)
    },
  })

  const onChangeUserSearch = debounce((value: string) => {
    setListUsers([])
    setCurrentPage(1)
    setSearch(value)
  }, 1000)

  const postSawitPoin = async (values: LoyaltyRequestPointDisbursementRequest) => {
    const selectionType = excludeUsers.length > 0 ? 'EXCLUDE' : selectAll ? 'ALL' : 'INCLUDE'
    const users =
      excludeUsers.length > 0
        ? excludeUsers.map(user => {
            return { userId: user?.id }
          })
        : selectAll
          ? []
          : includeUsers.map(user => {
              return { userId: user?.id }
            })
    const body = {
      department: values.department,
      disbursementTypeId: values.disbursementTypeId,
      purpose: values.purpose,
      title: values.title,
      description: values.description,
      point: Number(values.point),
      selectionType: selectionType as DisbursementRecipientSelectionTypeEnum,
      recipients: users,
    }
    try {
      setLoadingFetch(true)
      setFailedFetch(false)
      await loyaltyApi.loyaltyRequestPointDisbursement(body)
      setLoadingFetch(false)
      navigate('/sawitpoin', { state: { requestToast: 'success' } })
    } catch {
      setFailedFetch(true)
      setLoadingFetch(false)
      await toast('Permintaan gagal diajukan ke finance', 'danger')
    }
  }

  const fetchSawitPoinList = async () => {
    try {
      setLoadingFetch(true)
      const { data } = await loyaltyApi.getListLoyaltyPointDisbursementRequest()
      setListSawitPoin(data.items)
      setLoadingFetch(false)
    } catch (error) {
      setFailedFetch(true)
      setLoadingFetch(false)
    }
  }

  const fetchSawitPoinDetail = async (id: string) => {
    try {
      setLoadingFetch(true)
      const { data } = await loyaltyApi.getLoyaltyPointDisbursementRequest(id, 1, 10000)
      setPoinDetail(data)
      setLoadingFetch(false)
    } catch (error) {
      setFailedFetch(true)
      setLoadingFetch(false)
    }
  }

  const fetchDisbursementTypes = useCallback(async () => {
    try {
      setLoadingFetch(true)
      const { data } = await loyaltyApi.getLoyaltyPointDisbursementRequestData()
      setDisbursementTypes(data.disbursementTypes)
      setLoadingFetch(false)
    } catch {
      setLoadingFetch(false)
      setFailedFetch(true)
    }
  }, [])

  const fetchListUsers = async () => {
    setLoadingUser(true)
    try {
      const { data } = await adminsApi.adminsUserList(currentPage, 15, search)
      setListUsers([...listUsers, ...data.items])
      setTotalPages(data.pagination.pages)
      setTotalUsers(data.pagination.total)
    } catch (error) {
      setFailedFetch(true)
    }
    setLoadingUser(false)
  }

  const confirmRequestSawitPoin = async (isApproved: boolean, reason?: string) => {
    const body = {
      isApproved: isApproved,
      reason: reason ? reason : '',
    }
    try {
      setLoadingFetch(true)
      await loyaltyApi.confirmLoyaltyRequestPointDisbursement(poinDetail.id, body)
      setLoadingFetch(false)
      setModalConfirm(false)
      setModalReject(false)
      navigate('/sawitpoin', { state: { approveToast: 'success' } })
    } catch (error) {
      setModalConfirm(false)
      setModalReject(false)
      navigate('/sawitpoin', { state: { approveToast: 'failed' } })
    }
  }

  useEffect(() => {
    const container = tableContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [listUsers])

  const handleScroll = () => {
    const container = tableContainerRef.current

    if (container) {
      const scrollTop = container.scrollTop
      const contentHeight = container.scrollHeight
      const tableHeight = container.offsetHeight

      if (
        Math.ceil(scrollTop) + Math.ceil(tableHeight) >= contentHeight &&
        currentPage < totalPages
      ) {
        setCurrentPage(currentPage + 1)
      }
    }
  }

  return {
    tableContainerRef,
    onChangeUserSearch,
    loadingFetch,
    loadingUser,
    failedFetch,
    includeUsers,
    setIncludeUsers,
    excludeUsers,
    setExcludeUsers,
    selectAll,
    setSelectAll,
    departmentName,
    fetchSawitPoinDetail,
    confirmRequestSawitPoin,
    setLoadingFetch,
    setFailedFetch,
    fetchDisbursementTypes,
    fetchListUsers,
    fetchSawitPoinList,
    modalConfirm,
    setModalConfirm,
    modalReject,
    setModalReject,
    formData,
    setDataForm,
    disbursementTypes,
    listSawitPoin,
    listUsers,
    listUsersSelected,
    setListUsersSelected,
    setListUsers,
    poinDetail,
    formik,
    toastContainer,
    postSawitPoin,
    toast,
    currentPage,
    totalPages,
    totalUsers,
    setCurrentPage,
    search,
    setSearch,
  }
}

const departmentName = [
  {
    key: '1',
    name: 'Engineering',
  },
  {
    key: '2',
    name: 'Product',
  },
  {
    key: '3',
    name: 'HR',
  },
  {
    key: '4',
    name: 'Finance',
  },
  {
    key: '4',
    name: 'Experience',
  },
  {
    key: '4',
    name: 'Operations',
  },
]
